import { useState } from 'react';
import {
  ConnectAccountManagement,
  ConnectBalances,
  ConnectDisputesList,
  ConnectDocuments,
  ConnectNotificationBanner,
  ConnectPayments,
  ConnectPayouts,
} from '@stripe/react-connect-js';
import { Note, Panel } from '@wyocspay/ui';
import { BrandLogo } from './BrandLogo.js';
import { ConnectGate, useConnect } from './connect.js';
import { accountNeed, type AccountFacts } from './account-state.js';
import { summariseRequirements } from './requirements.js';

/**
 * The money and account surfaces, rendered by Stripe's embedded components.
 *
 * Why these are not hand-built tables: balances, payouts and disputes are
 * Stripe's state. Rebuilding them here would mean keeping a second copy of
 * someone else's ledger, which can only ever drift. The embedded components
 * show Stripe's own answer, support instant payouts and payout-schedule edits,
 * and keep the merchant inside WyoCSPay.
 */

export function BalancesView() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="These figures come from Stripe">
        Your funds sit on your own Stripe Connected Account. WyoCSPay does not hold your money and does
        not keep a second copy of your balance - what you see here is Stripe's own record.
      </Note>
      <ConnectGate what="Balances">
        <Panel>
          <ConnectBalances />
        </Panel>
      </ConnectGate>
    </div>
  );
}

export function PayoutsView() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Payouts settle from your Stripe balance to your bank">
        You can change your payout schedule and, where eligible, take an instant payout. WyoCSPay never
        moves the money itself - it is a Stripe payout from your account to your bank.
      </Note>
      <ConnectGate what="Payouts">
        <Panel>
          <ConnectPayouts />
        </Panel>
      </ConnectGate>
    </div>
  );
}

/**
 * Stripe's own payments view, alongside the WyoCSPay ledger.
 *
 * The two answer different questions: this one is the processor's record with
 * refund and dispute controls built in; the WyoCSPay Payments view shows our
 * domain state, attempt history and the lifecycle audit. Both are useful, and
 * showing both is more honest than pretending one is the whole picture.
 */
export function StripePaymentsView() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note>
        This is Stripe's record of your charges, with refunds and dispute handling built in. The{' '}
        <strong>Payments</strong> section shows the WyoCSPay view - attempt history and the audit trail
        for each payment.
      </Note>
      <ConnectGate what="Payments">
        <Panel>
          <ConnectPayments />
        </Panel>
      </ConnectGate>
    </div>
  );
}

export function DisputesView() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note tone="warn" title="Evidence deadlines are hard deadlines">
        A dispute you do not respond to before its deadline is lost by default. Submit evidence here;
        it goes straight to Stripe.
      </Note>
      <ConnectGate what="Disputes">
        <Panel>
          <ConnectDisputesList />
        </Panel>
      </ConnectGate>
    </div>
  );
}

/**
 * Account settings and documents.
 *
 * Spec: "WyoCSPay must not manually collect or validate merchant identity
 * documents." Everything below is a Stripe-hosted iframe - bank details,
 * business information and verification documents go from the merchant to
 * Stripe, and this application has no field or endpoint for any of them.
 *
 * There was a third tab here holding Stripe's `ConnectTaxSettings` and
 * `ConnectTaxRegistrations`. It is gone, and the reason is written out where
 * the components were enabled, in routes/merchant-panel.ts: this platform takes
 * a final amount and sends no tax parameter with any payment, so a head office
 * address and a set of registrations entered here changed nothing, here or
 * anywhere else. A screen that asks a merchant to configure something no code
 * reads is worse than no screen at all, because they only find out afterwards.
 */
/**
 * `initialTab` lets another view send a merchant to the right tab rather than
 * to the section and a hunt. It is an initial value and nothing more: once here,
 * the tabs behave exactly as they did, and arriving from the sidebar passes
 * nothing and lands on the first one.
 *
 * Anything unrecognised lands on the first tab as well, which is what a stale
 * `tab=tax` link now does rather than showing an empty panel.
 */
export function AccountSettingsView({ initialTab }: { initialTab?: string | null } = {}) {
  const [tab, setTab] = useState<'account' | 'documents'>(
    initialTab === 'documents' ? 'documents' : 'account',
  );
  const { status } = useConnect();

  const tabs = [
    ['account', 'Business & bank details'],
    ['documents', 'Documents'],
  ] as const;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/*
        The logo is ours, so it sits above the Stripe surfaces rather than
        inside their tab strip.

        Two reasons, and neither is aesthetic. The note under this one says
        everything below it is collected and held by Stripe, which would stop
        being true if a WyoCSPay upload were tucked in among the tabs. And that
        tab strip only exists once Connect has loaded - a merchant whose
        onboarding is unfinished, or whose Connect session failed, would
        otherwise have no way to reach a feature that has nothing to do with
        Stripe at all.
      */}
      <BrandLogo />

      <Note title="Handled by Stripe, shown inside WyoCSPay">
        Your business details, bank accounts and verification documents are collected and verified by
        Stripe directly. WyoCSPay displays Stripe's interface but never receives or stores the
        underlying documents.
      </Note>

      {status === 'ready' ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`wc-btn wc-btn-sm ${tab === key ? '' : 'wc-btn-secondary'}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <ConnectGate what="Account settings">
        <Panel>
          {tab === 'account' ? <ConnectAccountManagement /> : <ConnectDocuments />}
        </Panel>
      </ConnectGate>
    </div>
  );
}

/**
 * The single place the panel tells a merchant that Stripe wants something.
 *
 * At most one thing appears here, ever, and on a healthy account nothing does.
 * That is the whole point of the component: the overview used to stack Stripe's
 * notification banner on top of our own "onboarding is not complete" warning, so
 * a merchant mid-verification read the same instruction twice in two voices, and
 * a verified merchant still had a permanent nav item and a page of checklists
 * insisting there was work to do.
 *
 * The two sources are now mutually exclusive rather than stacked. Ours speaks
 * while there is something we know about - charges not enabled, or a requirement
 * on the account. Stripe's speaks only in the silence afterwards, because it is
 * the one thing that can still surface a notice we have no field for: a risk
 * review, a document about to expire. It renders nothing when it has nothing.
 */
export function AccountNotice({
  merchant,
  onOpenVerification,
}: {
  merchant: AccountFacts;
  onOpenVerification: () => void;
}) {
  // Called before any early return: hooks may not sit behind a condition.
  const { status } = useConnect();
  const need = accountNeed(merchant);

  if (need === 'verification') {
    return (
      <Note tone="warn" title="Stripe has not enabled charges yet">
        You cannot accept payments until Stripe finishes checking your business. Identity documents
        go to Stripe directly - WyoCSPay never sees or stores them.
        {merchant.requirements_due.length > 0 ? (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            Still required: {summariseRequirements(merchant.requirements_due)}
          </div>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="wc-btn" onClick={onOpenVerification}>
            Finish verification
          </button>
        </div>
      </Note>
    );
  }

  if (need === 'requirements') {
    /**
     * Charges still work here, and the wording has to say so.
     *
     * Stripe asks for more without switching payments off - a document that has
     * expired, details it did not need until volume grew. A merchant who reads
     * this as "you have stopped trading" will panic about a working account;
     * one who reads it as nothing will ignore it until payouts stop.
     */
    return (
      <Note tone="warn" title="Stripe has asked you for more">
        Your account is still taking payments. Stripe needs this before a deadline it will state on
        the verification page, and payouts can be held until it has it.
        <div style={{ marginTop: 8, fontSize: 13 }}>
          Requested: {summariseRequirements(merchant.requirements_due)}
        </div>
        <div style={{ marginTop: 12 }}>
          <button type="button" className="wc-btn" onClick={onOpenVerification}>
            Open verification
          </button>
        </div>
      </Note>
    );
  }

  if (status !== 'ready') return null;
  return (
    <div style={{ marginBottom: 4 }}>
      <ConnectNotificationBanner />
    </div>
  );
}
